/**
 * Reorder or Delete Slides Tool - Schema Definitions
 * 
 * Zod schemas for reordering and deleting slides with user approval.
 * Moves are auto-approved, deletions require explicit user approval.
 */

import { z } from "zod";

// ═══════════════════════════════════════════════════════════════════════════
// OPERATION TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Operation type - move or delete
 */
export const OperationTypeSchema = z.enum(["move", "delete"]);

/**
 * Single slide operation
 * Identifies a slide by ID or index, then specifies action
 */
export const SlideOperationSchema = z.object({
    /** Slide ID (e.g., "slide_257") - preferred for accuracy */
    slideId: z.string()
        .optional()
        .describe('Slide ID (e.g., "slide_257"). More reliable than index since indices shift.'),
    
    /** One-based slide index (e.g., 1, 5, 12) */
    slideIndex: z.number()
        .int()
        .positive()
        .optional()
        .describe('One-based slide index. Use slideId when available for reliability.'),
    
    /** Action to perform */
    action: OperationTypeSchema
        .describe('"move" to reorder the slide, "delete" to remove it permanently'),
    
    /** Destination position (1-based) - required for move operations */
    newIndex: z.number()
        .int()
        .positive()
        .optional()
        .describe('For move: destination position (1-based). Slide moves TO this position.'),
        
    /** Optional reason for the operation (shown to user during approval) */
    reason: z.string()
        .optional()
        .describe('Brief reason for this operation (shown to user for approval)'),
        
}).refine(
    data => data.slideId !== undefined || data.slideIndex !== undefined,
    { message: "Either slideId or slideIndex must be provided" }
).refine(
    data => data.action !== "move" || data.newIndex !== undefined,
    { message: "newIndex is required for move operations" }
);

// ═══════════════════════════════════════════════════════════════════════════
// TOOL INPUT
// ═══════════════════════════════════════════════════════════════════════════

export const ReorderOrDeleteSlidesParamsSchema = z.object({
    operations: z.array(SlideOperationSchema)
        .min(1, "At least one operation required")
        .describe(`Array of slide operations to perform IN ORDER.

IMPORTANT: Operations are executed sequentially and indices SHIFT after each operation!
- Moving slide 5 → position 2: slides 2,3,4 shift to become 3,4,5
- Deleting slide 3: slides 4,5,6... shift to become 3,4,5...
- Plan operations with this in mind, or use slideId instead of slideIndex for reliability.

Example - move slide 5 to position 2, then delete what is now slide 8:
[
  { "slideIndex": 5, "action": "move", "newIndex": 2, "reason": "Move intro slide to beginning" },
  { "slideId": "slide_257", "action": "delete", "reason": "Duplicate of slide 3" }
]`),
});

// ═══════════════════════════════════════════════════════════════════════════
// OUTPUT TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Result of a single operation
 */
export interface OperationResult {
    slideId: string;
    action: "move" | "delete";
    success: boolean;
    error?: string;
    /** For moves: the final position after operation (1-based) */
    finalIndex?: number;
}

/**
 * Approval status for the overall operation
 */
export const ApprovalStatusSchema = z.enum([
    "completed",      // All operations approved and executed
    "partial",        // Some deletions were rejected by user
    "cancelled",      // User cancelled the entire operation
    "timeout",        // User didn't respond in time
]);

/**
 * Tool response
 */
export interface ReorderOrDeleteSlidesResponse {
    results: OperationResult[];
    status: "completed" | "partial" | "cancelled" | "timeout";
    /** Summary of what happened */
    summary: string;
    /** Operations that were rejected by user (deletions) */
    rejectedOperations?: SlideOperation[];
}

// ═══════════════════════════════════════════════════════════════════════════
// TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export type OperationType = z.infer<typeof OperationTypeSchema>;
export type SlideOperation = z.infer<typeof SlideOperationSchema>;
export type ReorderOrDeleteSlidesParams = z.infer<typeof ReorderOrDeleteSlidesParamsSchema>;
export type ApprovalStatus = z.infer<typeof ApprovalStatusSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// UI DATA TYPES (for approval component)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Single operation pending approval - includes slide preview
 */
export interface PendingOperation {
    /** Position in operations array */
    index: number;
    /** Original operation from params */
    operation: SlideOperation;
    /** Normalized slide ID */
    resolvedSlideId: string;
    /** Current slide index at time of resolution (1-based) */
    currentIndex: number;
    /** Slide title/description if available */
    slideTitle?: string;
    /** Base64 thumbnail for preview */
    slideImage?: string;
    /** true for deletes, false for moves (moves are auto-approved) */
    requiresApproval: boolean;
    /** User's decision (only relevant for deletes) */
    approved?: boolean;
}

/**
 * Data structure for the approval UI component
 */
export interface ReorderOrDeleteApprovalData {
    /** Unique ID for this approval session */
    id: string;
    /** All operations with resolved data */
    operations: PendingOperation[];
    /** Index of operation currently being shown (for deletions) */
    currentOperationIndex: number;
    /** Overall status */
    status: 'pending' | 'approved' | 'cancelled' | 'timeout';
}

